import { UnfoldLess, UnfoldMore } from '@mui/icons-material';
import { Typography } from '@mui/material';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Collapsed } from '@/components/Collapsed';
import { useAlertsStore } from '@/configs/store';
import { IS_V2_WRITE_ENABLED } from '@/configs/v2Write';
import { useLocalStore } from '@/hooks/useLocalStore';
import { useIssueDrawer } from '@/tracker/features/issueDrawer';
import { TaskFilterBar } from '@/tracker/features/taskFilters';
import { useTrackerStore } from '@/tracker/model';
import { TaskBasic } from '@/tracker/types';

import { BoardCard } from '../BoardCard';
import { ColumnQuickCreate } from '../ColumnQuickCreate';
import { LaneHeader } from '../LaneHeader';
import { SprintFilterSelect } from '../SprintFilterSelect';
import { SwimlanesButton } from '../SwimlanesButton';
import { BoardColumnWithTasks, BoardStore } from '../../model/BoardStore';
import { useBoardLayout } from '../../model/useBoardLayout';

import { groupByParent } from './groupByParent';

import s from './BoardContent.module.scss';

/** Вкладка «Доска»: колонки по статусам, быстрые фильтры по спринту, свимлейны */
const BoardContent = observer(() => {
  const alertsStore = useAlertsStore();
  const trackerStore = useTrackerStore();
  const layout = useBoardLayout();
  const { store } = useLocalStore(() => new BoardStore({ alertsStore, trackerStore, layout }));
  const { issueLink } = useIssueDrawer();
  // Свёрнутые строки — только на время просмотра
  const [collapsedLanes, setCollapsedLanes] = useState<Set<string>>(new Set());

  useEffect(() => {
    store.load();
  }, [store]);

  const { board } = layout;
  const { lanes } = store;

  const isAllCollapsed = !!lanes?.length && lanes.every((lane) => collapsedLanes.has(lane.key));

  const toggleAllLanes = () =>
    setCollapsedLanes(new Set(isAllCollapsed ? [] : lanes?.map((lane) => lane.key)));

  const toggleLane = (key: string) => {
    const next = new Set(collapsedLanes);

    if (!next.delete(key)) next.add(key);

    setCollapsedLanes(next);
  };

  // В свимлейнах по родителю группировка внутри ячейки повторяла бы заголовок строки
  const groupsOf = (issues: TaskBasic[]) =>
    store.swimlanes === 'parent' ? [{ parent: null, issues }] : groupByParent(issues);

  const renderCell = (column: BoardColumnWithTasks) => (
    <div key={column.id} className={s.board__cell}>
      {groupsOf(column.tasks).map(({ parent, issues }) => (
        <div key={parent?.key ?? 'none'} className={s.board__parent}>
          {parent && (
            <Link
              to={issueLink(parent.key)}
              className={s.board__parentTitle}
              title={parent.summary ?? undefined}
            >
              {parent.key} {parent.summary}
            </Link>
          )}
          {issues.map((issue) => (
            <BoardCard key={issue.key} issue={issue} estimateBy={board?.estimateBy ?? null} />
          ))}
        </div>
      ))}
    </div>
  );

  if (store.isError) {
    return (
      <div className={s.empty}>
        <Typography color="text.secondary">Не удалось загрузить спринты</Typography>
        <Button onClick={store.load}>Повторить</Button>
      </div>
    );
  }

  return (
    <div className={s.tab}>
      <div className={s.filters}>
        <SprintFilterSelect
          value={store.sprintFilter}
          sprints={store.sprints}
          canCreate={layout.canManage}
          isSaving={store.isSaving}
          onChange={store.setSprintFilter}
          onCreate={store.createSprint}
        />
        <TaskFilterBar
          model={store.filters}
          fields={['statuses', 'types', 'priorities', 'assignees', 'authors', 'queues']}
          // Статусы вне колонок на доске не видны — фильтровать по ним незачем
          statuses={(board?.columns ?? []).flatMap((column) => column.statuses)}
        />
        <SwimlanesButton value={store.swimlanes} onChange={store.setSwimlanes} />
      </div>

      <div
        className={cn(s.board, store.isContentLoading && s.board_loading, lanes && s.board_lanes)}
        style={{
          gridTemplateColumns: `repeat(${store.columns.length}, var(--board-column-width))`,
        }}
      >
        {store.columns.map((column) => (
          <div key={column.id} className={s.board__column}>
            <div className={s.board__columnTitle}>
              {column.name}
              <span className={s.board__total}>{column.tasks.length}</span>
              {IS_V2_WRITE_ENABLED && (
                <ColumnQuickCreate store={store} boardId={layout.boardId} column={column} />
              )}
            </div>
          </div>
        ))}
        {!lanes && store.columns.map(renderCell)}
        {!!lanes?.length && (
          <div className={s.board__lanesBar}>
            <button type="button" className={s.board__collapseAll} onClick={toggleAllLanes}>
              {isAllCollapsed ? <UnfoldMore fontSize="small" /> : <UnfoldLess fontSize="small" />}
              {isAllCollapsed ? 'Развернуть все' : 'Свернуть все'}
            </button>
          </div>
        )}
        {lanes?.map((lane) => (
          <Fragment key={lane.key}>
            <LaneHeader
              owner={lane.owner}
              total={lane.total}
              isCollapsed={collapsedLanes.has(lane.key)}
              onToggle={() => toggleLane(lane.key)}
            />
            {lane.columns.map((column) => (
              <Collapsed
                key={column.id}
                opened={!collapsedLanes.has(lane.key)}
                className={s.board__laneCell}
              >
                {renderCell(column)}
              </Collapsed>
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
});

export { BoardContent };
